/**
 * ConfirmDialog — a reusable confirmation modal that replaces native
 * `window.confirm()` and `alert()` with a polished MUI Dialog.
 *
 * Supports both "confirm/cancel" (destructive actions) and "acknowledge"
 * (alert replacement with a single OK button) modes.
 *
 * @example
 * // Two-button confirm
 * <ConfirmDialog
 *   open={showDelete}
 *   title="Delete case?"
 *   description="This action cannot be undone."
 *   confirmLabel="Delete"
 *   confirmColor="error"
 *   onConfirm={handleDelete}
 *   onCancel={() => setShowDelete(false)}
 * />
 *
 * // Single-button acknowledge (alert replacement)
 * <ConfirmDialog
 *   open={showAlert}
 *   title="Upload complete"
 *   description="All 42 records were imported."
 *   confirmLabel="OK"
 *   onConfirm={() => setShowAlert(false)}
 * />
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  type SxProps,
  type Theme,
} from '@mui/material';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface ConfirmDialogProps {
  /** Whether the dialog is visible */
  open: boolean;
  /** Dialog headline */
  title: string;
  /** Supporting body text (string or JSX) */
  description?: ReactNode;
  /** Label for the primary (right) button (default: "Confirm") */
  confirmLabel?: string;
  /** Label for the secondary (left) button (default: "Cancel") */
  cancelLabel?: string;
  /** MUI color for the confirm button (default: "primary") */
  confirmColor?: 'primary' | 'error' | 'warning' | 'success' | 'info';
  /** Called when the user confirms */
  onConfirm: () => void;
  /**
   * Called when the user cancels. If omitted, only the confirm
   * button is shown (alert / acknowledge mode).
   */
  onCancel?: () => void;
  /** Show a loading spinner on the confirm button */
  loading?: boolean;
  /** Extra sx on the Dialog paper */
  sx?: SxProps<Theme>;
}

export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirmColor = 'primary',
  onConfirm,
  onCancel,
  loading = false,
  sx,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('common.actions.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('common.actions.cancel');
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel ?? onConfirm}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx }}
      aria-labelledby="confirm-dialog-title"
    >
      <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>

      {description && (
        <DialogContent>
          {typeof description === 'string' ? (
            <DialogContentText>{description}</DialogContentText>
          ) : (
            description
          )}
        </DialogContent>
      )}

      <DialogActions>
        {onCancel && (
          <Button onClick={onCancel} disabled={loading}>
            {resolvedCancelLabel}
          </Button>
        )}
        <Button
          onClick={onConfirm}
          color={confirmColor}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          autoFocus
        >
          {resolvedConfirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
